import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Public $9 "Skip the line" one-click Stripe checkout.
 * Lives on the Landing page — no auth required.
 * On success:
 *   - Buyer is boosted to top of the waitlist
 *   - First 50 buyers get a permanent Founding Member badge
 *   - Share bar drops so the buyer becomes a distributor
 */

const SHARE_LINK = "https://moneyhubvip.com";
const SHARE_COPY = "I just went VIP on MoneyHub — 5 income streams, 1 dashboard. $9 lifetime early access:";

const SHARE_TARGETS = [
  {
    key: "x",
    label: "X",
    href: (u, t) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}`,
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    href: (u, t) => `https://api.whatsapp.com/send?text=${encodeURIComponent(`${t} ${u}`)}`,
  },
  {
    key: "sms",
    label: "SMS",
    href: (u, t) => `sms:?&body=${encodeURIComponent(`${t} ${u}`)}`,
  },
  {
    key: "email",
    label: "Email",
    href: (u, t) => `mailto:?subject=${encodeURIComponent("Check this out")}&body=${encodeURIComponent(`${t} ${u}`)}`,
  },
  {
    key: "facebook",
    label: "Facebook",
    href: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`,
  },
  {
    key: "reddit",
    label: "Reddit",
    href: (u, t) => `https://www.reddit.com/submit?url=${encodeURIComponent(u)}&title=${encodeURIComponent(t)}`,
  },
];

const ShareBar = () => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${SHARE_COPY} ${SHARE_LINK}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      /* clipboard blocked */
    }
  };
  return (
    <div data-testid="skip-line-share-bar" className="mt-4 pt-4 border-t border-[#22c55e]/30">
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono mb-3">
        // Every share = another VIP. Help us grow.
      </div>
      <div className="flex flex-wrap gap-2">
        {SHARE_TARGETS.map((t) => (
          <a
            key={t.key}
            data-testid={`share-${t.key}`}
            href={t.href(SHARE_LINK, SHARE_COPY)}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-mono font-bold text-white bg-black/40 border border-zinc-700 hover:border-[#22c55e] hover:text-[#22c55e] rounded transition-colors"
          >
            {t.label}
          </a>
        ))}
        <button
          type="button"
          data-testid="share-copy"
          onClick={copy}
          className="px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-mono font-bold text-white bg-black/40 border border-zinc-700 hover:border-[#22c55e] hover:text-[#22c55e] rounded transition-colors"
        >
          {copied ? "Copied ✓" : "Copy link"}
        </button>
      </div>
    </div>
  );
};

export const SkipTheLine = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // null | 'success' | 'cancelled'
  const [slot, setSlot] = useState(null);
  const [isFounding, setIsFounding] = useState(false);
  const [foundingRemaining, setFoundingRemaining] = useState(null);

  // On mount, fetch founding-member counter for the pre-purchase pitch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/public/founding-members");
        if (cancelled) return;
        if (typeof r?.data?.remaining === "number") {
          setFoundingRemaining(r.data.remaining);
        }
      } catch (_) {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Detect return-from-Stripe on mount.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const sid = q.get("skipped_the_line");
    if (q.get("skip_cancelled")) {
      setStatus("cancelled");
      toast.info("Checkout cancelled — you can try again anytime.");
      return;
    }
    if (!sid) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/public-status/${sid}`);
        if (cancelled) return;
        if (r?.data?.payment_status === "paid") {
          setStatus("success");
          setSlot(r?.data?.slot_num || null);
          setIsFounding(!!r?.data?.is_founding_member);
          toast.success("✓ Early access granted! Check your email for the sign-up link.");
        } else if (["expired", "unpaid"].includes(r?.data?.payment_status)) {
          setStatus("cancelled");
        } else {
          setTimeout(poll, 2000);
        }
      } catch (_) {
        /* silent - transient */
      }
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  const buy = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/payments/skip-the-line", {
        origin_url: window.location.origin,
        email: email.trim(),
      });
      if (r?.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Couldn't start checkout. Try again in a moment.");
        setBusy(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong.";
      toast.error(typeof msg === "string" ? msg : "Couldn't start checkout.");
      setBusy(false);
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="skip-line-success"
        className="border border-[#22c55e]/50 bg-[#22c55e]/10 text-[#22c55e] font-mono text-sm px-4 py-5 max-w-xl mx-auto rounded"
      >
        <div className="text-base font-bold">✓ You're in — VIP early access.</div>
        {isFounding && slot && (
          <div
            data-testid="founding-member-callout"
            className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-black tracking-wider uppercase bg-gradient-to-r from-[#facc15] via-[#fde047] to-[#facc15] text-black shadow-[0_0_25px_rgba(250,204,21,0.35)]"
          >
            <span>◆ Founding Member #{slot} · Gold badge</span>
          </div>
        )}
        <div className="mt-2 text-[#22c55e]/90 text-xs">
          We're sending your sign-up link to <span className="font-bold">{email || "your email"}</span> now.
          Your waitlist rank was boosted to #1.
        </div>
        <ShareBar />
      </div>
    );
  }

  const remainingText =
    foundingRemaining !== null
      ? foundingRemaining > 0
        ? `Only ${foundingRemaining} Founding Member gold badges left.`
        : "All Founding Member slots claimed — early access still $9."
      : "";

  return (
    <div
      data-testid="skip-the-line-card"
      className="border border-[#60a5fa]/40 bg-gradient-to-br from-[#60a5fa]/10 via-black to-[#a855f7]/10 px-6 py-6 max-w-xl mx-auto rounded"
    >
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] font-mono">
          // Instant · lifetime early access
        </div>
        <div className="font-heading text-2xl font-black text-[#22c55e]">$9</div>
      </div>
      <div className="font-heading text-xl font-black tracking-tight mb-2">
        Skip the line — go VIP now.
      </div>
      <p className="text-xs text-zinc-300 leading-relaxed mb-3">
        One-time $9. Lifetime early access when payments unlock. Your waitlist rank
        jumps to <span className="text-white font-bold">#1</span> immediately, and
        you keep every referral bonus you earn along the way.
      </p>
      {remainingText && (
        <div
          data-testid="founding-remaining"
          className="mb-4 text-[10px] tracking-[0.25em] uppercase text-[#facc15] font-mono"
        >
          ◆ {remainingText}
        </div>
      )}
      <form onSubmit={buy} className="flex flex-col sm:flex-row items-stretch gap-2">
        <input
          data-testid="skip-line-email"
          type="email"
          required
          autoComplete="email"
          placeholder="you@anywhere.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
          className="w-full bg-black border border-zinc-700 focus:border-[#60a5fa] outline-none px-4 py-3 text-white font-mono text-sm placeholder:text-zinc-500 disabled:opacity-50 rounded"
        />
        <button
          data-testid="skip-line-buy-btn"
          type="submit"
          disabled={busy || !email.trim()}
          className="w-full sm:w-auto shrink-0 text-[10px] tracking-[0.3em] uppercase text-black bg-[#22c55e] hover:bg-[#4ade80] px-6 py-3 font-black disabled:opacity-50 transition-colors rounded"
        >
          {busy ? "Loading…" : "Skip the line · $9"}
        </button>
      </form>
      <div className="mt-3 text-[9px] tracking-[0.25em] uppercase text-zinc-500 font-mono">
        // Powered by Stripe · secure card entry
      </div>
    </div>
  );
};

export default SkipTheLine;
